import { LANGUAGES_FOR_ELEVENLABS, MULTILINGUAL } from '@/constants';
import { useAllVoices } from '@/hooks/useAllVoices';
import { isProPlus } from '@/pages/AdvancedEditor/components/panels/script/utils';
import { useGetCurrentAgentsPresenter } from '@/pages/agents/hooks';
import { languageToVoicesMap as _languageToVoicesMap } from '@/state/presenters';
import { useAgentEditorStore } from '@/state/root';
import { IVoice, Providers } from '@d-id/types';
import { useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { isMsDeprecatedVoice, voiceOptionsForMultiLingual } from '../utils';

export const useGetAllVoices = () => {
    const languageToVoicesMap = useRecoilValue(_languageToVoicesMap);

    useAllVoices(!Object.keys(languageToVoicesMap)?.length, [Providers.Elevenlabs, Providers.Microsoft, Providers.AzureOpenAi]);
    return languageToVoicesMap;
};

export const useGetAllVoicesFlat = () => {
    const voiceOptions = useGetAllVoices();
    return Object.values(voiceOptions).flat();
};

const FEMALE_SORT = 1;
const MALE_SORT = -1;

export const useGetVoiceOptions = () => {
    const agentEditorStore = useAgentEditorStore();

    useGetAllVoices();
    const languageToVoicesMap = useRecoilValue(_languageToVoicesMap);
    const presenter = useGetCurrentAgentsPresenter();
    const presenterSorting = presenter?.gender === 'female' ? FEMALE_SORT : MALE_SORT;

    return useMemo(() => {
        const { ttsSelectedLanguage } = agentEditorStore;
        if (!Object.keys(languageToVoicesMap).length || !ttsSelectedLanguage) {
            return [];
        }

        let voices: IVoice[] = [];
        if (ttsSelectedLanguage === MULTILINGUAL) {
            voices = voiceOptionsForMultiLingual(languageToVoicesMap);
        } else {
            const trimmedSelectedLanguage = ttsSelectedLanguage.split(' ')?.[0];
            languageToVoicesMap[trimmedSelectedLanguage]?.forEach(voice => {
                if (LANGUAGES_FOR_ELEVENLABS.includes(voice?.language?.split(' ')?.[0]!)) {
                    voices.push(voice);
                }
            });
            languageToVoicesMap[ttsSelectedLanguage]?.forEach(voice => {
                if (
                    LANGUAGES_FOR_ELEVENLABS.includes(voice?.language?.split(' ')?.[0]!) ||
                    [Providers.Microsoft, Providers.AzureOpenAi].includes(voice.provider)
                ) {
                    voices.push(voice);
                }
            });
        }

        // Filter out MS deprecated voices
        const filteredVoices = voices.filter(voice => !isMsDeprecatedVoice(voice));

        // Dedup duplicated voice options
        const uniqueVoices = Array.from(
            filteredVoices
                .reduce((map, obj) => {
                    map.set(obj.id, obj);
                    return map;
                }, new Map())
                .values()
        );

        return [...uniqueVoices]
            .sort(voice => (isProPlus(voice) ? MALE_SORT : FEMALE_SORT))
            .sort(voice => ([Providers.Microsoft, Providers.AzureOpenAi].includes(voice.provider) ? -1 : 1))
            .sort((a, b) => {
                return presenterSorting * a.gender.localeCompare(b.gender);
            });
    }, [presenterSorting, languageToVoicesMap, agentEditorStore.ttsSelectedLanguage]);
};
